/**
 * localStorage persistence for tournament state.
 */

import type { TournamentState } from './types';

const STORAGE_KEY_PREFIX = 'tournament-';
const DRAFT_KEY_PREFIX = 'draft-ratings-';
const MODE_KEY = 'app-mode';

/**
 * Draft rating for a country (used before "Confirm & Continue").
 */
export interface DraftRating {
  code: string;
  stars: number | null;
}

/**
 * Get storage key for draft ratings of a specific group.
 */
function getDraftKey(scopeId: string, groupId: string): string {
  return `${DRAFT_KEY_PREFIX}${scopeId}-${groupId}`;
}

/**
 * Save draft ratings for a group.
 * Called immediately when user changes any rating.
 */
export function saveDraftRatings(scopeId: string, groupId: string, ratings: DraftRating[]): void {
  const key = getDraftKey(scopeId, groupId);
  localStorage.setItem(key, JSON.stringify(ratings));
}

/**
 * Load draft ratings for a group.
 * Returns null if no draft exists.
 */
export function loadDraftRatings(scopeId: string, groupId: string): DraftRating[] | null {
  const key = getDraftKey(scopeId, groupId);
  const data = localStorage.getItem(key);
  if (!data) return null;

  try {
    return JSON.parse(data) as DraftRating[];
  } catch {
    return null;
  }
}

/**
 * Clear draft ratings for a specific group.
 * Called after successful submission.
 */
export function clearDraftRatings(scopeId: string, groupId: string): void {
  const key = getDraftKey(scopeId, groupId);
  localStorage.removeItem(key);
}

/**
 * Clear all draft ratings for a scope.
 * Called when starting a new tournament.
 */
export function clearAllDraftRatings(scopeId: string): void {
  const keysToRemove: string[] = [];

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith(`${DRAFT_KEY_PREFIX}${scopeId}-`)) {
      keysToRemove.push(key);
    }
  }

  keysToRemove.forEach(key => localStorage.removeItem(key));
}

/**
 * Get the storage key for a scope's tournament.
 */
function getStorageKey(scopeId: string): string {
  return `${STORAGE_KEY_PREFIX}${scopeId}`;
}

/**
 * Save tournament state to localStorage.
 */
export function saveTournamentState(state: TournamentState): void {
  const key = getStorageKey(state.scopeId);
  const serialized = JSON.stringify(state);
  localStorage.setItem(key, serialized);
}

/**
 * Load tournament state from localStorage.
 */
export function loadTournamentState(scopeId: string): TournamentState | null {
  const key = getStorageKey(scopeId);
  const serialized = localStorage.getItem(key);

  if (!serialized) return null;

  try {
    const state = JSON.parse(serialized) as TournamentState;

    // Validate basic structure
    if (
      typeof state.id !== 'string' ||
      typeof state.scopeId !== 'string' ||
      typeof state.status !== 'string'
    ) {
      console.warn('Invalid tournament state in localStorage, clearing');
      clearTournamentState(scopeId);
      return null;
    }

    return state;
  } catch (error) {
    console.warn('Failed to parse tournament state from localStorage', error);
    clearTournamentState(scopeId);
    return null;
  }
}

/**
 * Clear tournament state from localStorage.
 */
export function clearTournamentState(scopeId: string): void {
  const key = getStorageKey(scopeId);
  localStorage.removeItem(key);
}

/**
 * Check if there's an active (non-completed) tournament for a scope.
 */
export function hasActiveTournament(scopeId: string): boolean {
  const state = loadTournamentState(scopeId);
  return state !== null && state.status !== 'completed';
}

/**
 * Get all scopes that have active tournaments.
 */
export function getActiveTournamentScopes(): string[] {
  const scopes: string[] = [];

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith(STORAGE_KEY_PREFIX)) {
      const scopeId = key.slice(STORAGE_KEY_PREFIX.length);
      if (hasActiveTournament(scopeId)) {
        scopes.push(scopeId);
      }
    }
  }

  return scopes;
}

/**
 * Save app mode to localStorage.
 */
export function saveAppMode(mode: 'list' | 'tournament'): void {
  localStorage.setItem(MODE_KEY, mode);
}

/**
 * Load app mode from localStorage.
 */
export function loadAppMode(): 'list' | 'tournament' {
  const mode = localStorage.getItem(MODE_KEY);
  if (mode === 'tournament') return 'tournament';
  return 'list';
}

/**
 * Get tournament progress for a scope.
 * Returns how many countries have been rated/ranked out of total.
 */
export function getTournamentProgress(scopeId: string): {
  rated: number;
  total: number;
  percentage: number;
  status: 'not_started' | 'filter_stage' | 'group_stage' | 'bracket_stage' | 'completed';
} {
  const state = loadTournamentState(scopeId);

  if (!state) {
    return { rated: 0, total: 0, percentage: 0, status: 'not_started' };
  }

  if (state.status === 'filter_stage') {
    // Filter stage: no countries rated yet, show selection progress
    const selected = state.filterSelection?.length ?? 0;
    return {
      rated: selected,
      total: state.totalCountries,
      percentage: 0, // Don't show percentage during filter
      status: 'filter_stage',
    };
  }

  const total = state.totalCountries;

  if (state.status === 'completed' && state.finalRankings) {
    // All countries have been rated
    return {
      rated: state.finalRankings.length,
      total,
      percentage: 100,
      status: 'completed',
    };
  }

  if (state.status === 'bracket_stage') {
    // Count eliminated + bracket matches decided
    const eliminatedCount = state.eliminatedInGroups.length;
    const decidedMatches = state.bracket?.filter(m => m.winner !== null).length ?? 0;
    // Each decided match effectively "rates" one more country (the loser)
    // Plus we add 1 for the current advancing countries being in bracket
    const bracketParticipants = state.advancingCountries.length;
    // Estimated: eliminated are rated, bracket participants partially rated
    const rated = eliminatedCount + Math.min(decidedMatches, bracketParticipants);
    return {
      rated,
      total,
      percentage: Math.round((rated / total) * 100),
      status: 'bracket_stage',
    };
  }

  if (state.status === 'group_stage') {
    // Count eliminated countries (they have final ratings)
    // Plus count countries in completed groups this round
    const eliminatedCount = state.eliminatedInGroups.length;
    const completedGroupsThisRound = state.currentGroupIndex;
    // Each completed group rates some countries
    const avgGroupSize = total > 0 ? Math.ceil(state.advancingCountries.length / Math.max(1, state.groupRound)) : 5;
    const ratedThisRound = completedGroupsThisRound * Math.ceil(avgGroupSize * 0.6); // ~60% get eliminated per group
    const rated = eliminatedCount + Math.min(ratedThisRound, total - eliminatedCount);
    return {
      rated: Math.min(rated, total),
      total,
      percentage: Math.round((Math.min(rated, total) / total) * 100),
      status: 'group_stage',
    };
  }

  return { rated: 0, total, percentage: 0, status: state.status };
}
